import { Alert, Box, Checkbox, Chip, FormControlLabel, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { toSmsSafe } from '../../../lib/smsSafeText';
import { describeSmsEstimate, smsEstimateDetail, smsOverLimitMessage, type SmsEstimate } from '../../../lib/smsEstimate';
import {
  BODY_MAX,
  SMS_MAX,
  STEP_PRESETS,
  SUBJECT_MAX,
  audienceKindPhrase,
  stepIsPast,
  type PlanStepDraft,
} from '../../../lib/reminderPlan';

/**
 * One step of a reminder plan: a tick, a trip-local date/time, and the copy the
 * step will send — all editable. A step whose time has passed is unticked AND
 * disabled (it cannot be scheduled), but its date field stays editable so the
 * host can move it to a real future time, which turns it back on.
 */
export default function PlanStepEditor({
  draft,
  timeZone,
  smsOn,
  smsEstimate,
  errors,
  onChange,
  onWhen,
}: {
  draft: PlanStepDraft;
  timeZone: string;
  smsOn: boolean;
  /** How many texts this step's message will be per guest (the server's count, or a labelled local one while it is asked). */
  smsEstimate: SmsEstimate | null;
  /** What the server refused about THIS step (every failing step is reported, each next to its own editor). */
  errors?: readonly string[];
  onChange: (patch: Partial<PlanStepDraft>) => void;
  onWhen: (when: string) => void;
}) {
  const preset = STEP_PRESETS.find((candidate) => candidate.key === draft.key)!;
  const past = stepIsPast(draft.when, timeZone);
  const smsSafe = toSmsSafe(draft.smsBody).text;
  const smsSimplified = smsSafe !== draft.smsBody;
  const smsSent = smsEstimate;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, opacity: draft.enabled ? 1 : 0.75 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <FormControlLabel
          control={(
            <Checkbox
              checked={draft.enabled && !past}
              disabled={past}
              onChange={(e) => onChange({ enabled: e.target.checked })}
              inputProps={{ 'aria-label': `Send ${preset.label}` }}
            />
          )}
          label={<Typography fontWeight={700}>{preset.label}</Typography>}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          {past ? <Chip size="small" label="In the past" /> : null}
          <Typography variant="caption" color="text.secondary">
            To: {audienceKindPhrase(preset.audienceKind)}
          </Typography>
        </Stack>
      </Stack>
      {errors && errors.length > 0 ? (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="plan-step-error">
          {errors.map((message) => <div key={message}>{message}</div>)}
        </Alert>
      ) : null}
      <Box sx={{ mt: 1 }}>
        <TextField
          size="small"
          type="datetime-local"
          label={`When (${timeZone})`}
          value={draft.when}
          onChange={(e) => onWhen(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ maxWidth: 260 }}
          helperText={past ? 'That time has passed — pick a later one to use this step.' : 'Local time at the trip.'}
        />
      </Box>
      {draft.enabled && !past ? (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            label="Subject"
            value={draft.subject}
            onChange={(e) => onChange({ subject: e.target.value.slice(0, SUBJECT_MAX) })}
            helperText={`${draft.subject.length}/${SUBJECT_MAX}`}
          />
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={4}
            label="Email message"
            value={draft.body}
            onChange={(e) => onChange({ body: e.target.value.slice(0, BODY_MAX) })}
            helperText={`${draft.body.length}/${BODY_MAX} · Variables: {name} {title} {location} {url}`}
          />
          {smsOn ? (
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              label="Text message"
              value={draft.smsBody}
              onChange={(e) => onChange({ smsBody: e.target.value.slice(0, SMS_MAX) })}
              error={smsSafe.length > SMS_MAX || Boolean(smsSent?.source === 'server' && smsSent.overLimit)}
              helperText={(
                <>
                  {`${smsSafe.length}/${SMS_MAX}`}
                  {smsSent ? (
                    <>
                      {' · '}
                      <Tooltip title={smsEstimateDetail(smsSent)}>
                        <span>{describeSmsEstimate(smsSent)}</span>
                      </Tooltip>
                    </>
                  ) : null}
                  {'. The trip link is added at the end automatically.'}
                  {smsSimplified ? ' Some characters will be simplified for SMS.' : ''}
                  {smsSent?.overLimit ? ` ${smsOverLimitMessage(smsSent)}` : ''}
                </>
              )}
            />
          ) : null}
        </Stack>
      ) : null}
    </Paper>
  );
}
